import { useState, type ReactNode } from 'react'
import { Box, Link, Stack, Typography } from '@mui/material'
import { ArrowUpRightIcon } from 'lucide-react'
import {
  CONTENT_GRAPHIC_KIND_LABELS,
  CONTENT_IDEA_TYPE_LABELS,
  type ContentIdea,
  type ContentRecommendation,
  type ContentRecommendationTerm,
} from '@repo/types'
import { Button, Input } from '@repo/ui'

import { Term } from 'components/feedback/Term'
import { CONTENT_IDEA_STATES } from 'constants/statuses'
import { useSnackbar } from 'contexts'
import { useDecideContentRecommendation } from 'hooks/api'

import { industryLabel, MANAGE_IDEAS_TIP, solutionLabel } from './contentIdeaModel'


const Label = ({ children }: { children: ReactNode }) => (
  <Typography variant="body4" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
    {children}
  </Typography>
)
const Line = ({ children, muted }: { children: ReactNode; muted?: boolean }) => (
  <Typography variant="body3" component="div" color={muted ? 'text.secondary' : undefined} sx={{ lineHeight: 1.55 }}>
    {children}
  </Typography>
)
const vol = (t: ContentRecommendationTerm) =>
  t.volume === null ? 'unmeasured' : `${t.volume.toLocaleString('en-US')}/mo${t.market ? ` ${t.market.toUpperCase()}` : ''}`

/**
 * VRG-741 — the RECOMMENDATION card "Suggest 5 more" assembled for an idea (templates + the
 * database, no model call): title, summary, type by the site rule, primary and secondary terms with
 * volume, industries and solutions, planned graphics with the corpus source that supplies each, the
 * research and the outline. "Write this" selects the idea (Brief; the one approval is on the draft); Reject asks for a reason and
 * the idea's terms are never proposed again.
 */
export const RecommendationCard = ({ idea, canEdit }: { idea: ContentIdea; canEdit: boolean }) => {
  const card = idea.recommendation as ContentRecommendation
  const decide = useDecideContentRecommendation()
  const { open: notify } = useSnackbar()
  const [rejecting, setRejecting] = useState(false)
  const [reason, setReason] = useState('')
  const open = idea.status === 'idea'

  const act = (decision: 'approve' | 'reject') =>
    decide.mutate(
      { id: idea.id, input: { decision, reason: decision === 'reject' ? reason.trim() : null } },
      {
        onSuccess: () => {
          notify({
            type: 'success',
            text: decision === 'approve' ? `“${card.title}” is selected to write (Brief).` : `Rejected “${card.title}”.`,
          })
          setRejecting(false)
          setReason('')
        },
        onError: (e) => notify({ type: 'error', text: (e as Error).message }),
      },
    )

  return (
    <Box
      component="section"
      aria-label="Recommendation"
      sx={(t) => ({ border: `1px solid ${t.palette.divider}`, borderRadius: 2, p: 4 })}
    >
      <Stack spacing={3}>
        <Stack spacing={0.5}>
          <Label>Recommendation · {CONTENT_IDEA_TYPE_LABELS[card.type]}</Label>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {card.title}
          </Typography>
          <Line muted>{card.summary}</Line>
        </Stack>

        <Stack spacing={1}>
          <Label>Type</Label>
          <Line>
            {CONTENT_IDEA_TYPE_LABELS[card.type]}. <span style={{ opacity: 0.7 }}>{card.type_reason}</span>
          </Line>
        </Stack>

        <Stack spacing={1}>
          <Label>Terms</Label>
          {card.primary_term ? (
            <Line>
              <strong>{card.primary_term.term}</strong> · {vol(card.primary_term)} · primary
            </Line>
          ) : (
            <Line muted>No primary term.</Line>
          )}
          {card.secondary_terms.map((t) => (
            <Line key={t.term} muted>
              {t.term} · {vol(t)}
            </Line>
          ))}
        </Stack>

        <Stack spacing={1}>
          <Label>Industries and solutions</Label>
          <Line muted={!card.industries.length && !card.solutions.length}>
            {[...card.industries.map(industryLabel), ...card.solutions.map(solutionLabel)].join(' · ') ||
              'Not tagged.'}
          </Line>
        </Stack>

        <Stack spacing={1}>
          <Label>Planned graphics ({card.graphics.length})</Label>
          {card.graphics.map((g, i) => (
            <Stack key={`${g.kind}-${i}`} spacing={0.25}>
              <Line>
                <strong>{CONTENT_GRAPHIC_KIND_LABELS[g.kind]}</strong> · {g.section}
                {g.needs_chart_block ? (
                  <Typography component="span" variant="body4" color="warning.main" sx={{ ml: 1 }}>
                    <Term id="content-needs-chart-block">Needs chart block</Term>
                  </Typography>
                ) : null}
              </Line>
              <Line muted>{g.shows}</Line>
              <Typography variant="body4" color="text.secondary" noWrap>
                Data from{' '}
                <Link href={g.source.url} target="_blank" rel="noreferrer noopener" color="inherit">
                  {g.source.title ?? g.source.url}
                </Link>
              </Typography>
            </Stack>
          ))}
          {card.graphics_note ? <Line muted>{card.graphics_note}</Line> : null}
        </Stack>

        <Stack spacing={1}>
          <Label>Research ({card.sources.length})</Label>
          {card.sources.map((s) => (
            <Typography key={s.market_item_id} variant="body3" noWrap title={s.title ?? s.url}>
              <Link
                href={s.url}
                target="_blank"
                rel="noreferrer noopener"
                color="inherit"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
              >
                {s.title ?? s.url}
                <ArrowUpRightIcon strokeWidth={1} size={14} style={{ opacity: 0.6 }} />
              </Link>
              <Typography component="span" variant="body4" color="text.secondary">
                {' '}
                {[s.publication, s.published_at?.slice(0, 10)].filter(Boolean).join(' · ')}
              </Typography>
            </Typography>
          ))}
        </Stack>

        <Stack spacing={1}>
          <Label>Outline</Label>
          <Box component="ol" sx={{ m: 0, pl: 5 }}>
            {card.outline.map((o) => (
              <Box component="li" key={o.heading} sx={{ mb: 1 }}>
                <Line>
                  <strong>{o.heading}</strong>
                  {o.research.length ? (
                    <Typography component="span" variant="body4" color="text.secondary">
                      {' '}
                      · {o.research.length} source{o.research.length === 1 ? '' : 's'}
                    </Typography>
                  ) : null}
                </Line>
                <Line muted>{o.guidance}</Line>
              </Box>
            ))}
          </Box>
          <Typography variant="body4" color="text.secondary">
            Follows {card.style_guide}. Assembled {card.generated_at.slice(0, 10)} without a model call.
          </Typography>
        </Stack>

        {open ? (
          rejecting ? (
            <Stack spacing={2}>
              <Input label="Why not" value={reason} onChange={setReason} multiline minRows={2} />
              <Stack direction="row" spacing={3}>
                <Button variant="secondary" onClick={() => setRejecting(false)} disabled={decide.isPending}>
                  Cancel
                </Button>
                <Button variant="primary" loading={decide.isPending} disabled={!reason.trim()} onClick={() => act('reject')}>
                  Reject recommendation
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Stack direction="row" spacing={3}>
              <Button
                variant="secondary"
                disabled={!canEdit || decide.isPending}
                disabledReason={canEdit ? undefined : MANAGE_IDEAS_TIP}
                onClick={() => setRejecting(true)}
              >
                Reject
              </Button>
              <Button
                variant="primary"
                disabled={!canEdit || decide.isPending}
                disabledReason={canEdit ? undefined : MANAGE_IDEAS_TIP}
                loading={decide.isPending}
                onClick={() => act('approve')}
              >
                Write this
              </Button>
            </Stack>
          )
        ) : (
          <Line muted>
            Decided: the idea is at {CONTENT_IDEA_STATES[idea.status]?.label ?? idea.status}.
          </Line>
        )}
      </Stack>
    </Box>
  )
}
